import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_RETENTION_DAYS,
  SORT_VOICE_OPTIONS_COMMUNITY,
  SORT_VOICE_OPTIONS_MY_VOICE,
  SORT_VOICE_OPTIONS_VBEE,
  SORT_VOICE_OPTIONS_FAVOURITE,
} from '@src/constants/voice';
import { omit } from 'lodash';
import { useHistory } from 'react-router-dom';
import { AddRounded } from '@mui/icons-material';
import Typography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';
import { VOICE_CLONING_TYPE } from '@src/constants/voiceCloning';
import Button from '@src/new-components/Button';
import ROUTES from '@src/constants/route';
import useAgeGroupVoiceCloning from '@src/hooks/useAgeGroupVoiceCloning';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { useSelector } from 'react-redux';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { StyledFilter, StyledListTags } from './index.style';
import Accent from './Accent';
import Language from './Language';
import AdvancedFilterButton from './AdvancedFilterButton';
import SortVoice from './SortVoice';
import Search from './Search';
import Category from './Category';
import Tag from './Tag';

const Filter = ({
  dataLanguages,
  dataFilter,
  handleChangeFilter,
  isVbee,
  isFavourite,
  resetDataFilter,
  searchValueView,
  setSearchValueView,
  isMyVoice,
  activeTab,
  isOpenFromSpeakerSettings = false,
  onCloseWhenCreateVoice,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const history = useHistory();

  const { user } = useSelector((state) => state.auth);
  const { getFeatureValue } = useFeatureFlags();

  const premiumVoiceEnabled = getFeatureValue(FEATURE_KEYS.PREMIUM_VOICE, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const gen2VietnamVoiceEnabled = getFeatureValue(
    FEATURE_KEYS.GEN2_VIETNAM_VOICE,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const { ageGroupOptions } = useAgeGroupVoiceCloning();

  const handleCreateVoice = () => {
    history.push({
      pathname: ROUTES.VOICE_CLONING_VOICES,
      state: { type: VOICE_CLONING_TYPE.CREATE },
    });
    if (isOpenFromSpeakerSettings && onCloseWhenCreateVoice) {
      onCloseWhenCreateVoice();
    }
  };

  const handleRemoveFilter = (key, value) => {
    if (key === 'categories') {
      handleChangeFilter('categories', value);
    } else {
      handleChangeFilter(key, value);
    }
  };

  const ageGroupOptionsTranslated = ageGroupOptions?.map((item) => ({
    ...item,
    label: t(item.label),
  }));

  const advancedFilterOptions = {
    gender: [
      { label: t('male'), value: 'male' },
      { label: t('female'), value: 'female' },
    ],
    ageGroup: ageGroupOptionsTranslated,
    level: [
      { label: t('BASIC'), value: 'BASIC' },
      { label: t('ADVANCED'), value: 'ADVANCED' },
      ...(gen2VietnamVoiceEnabled ? [{ label: t('GEN2'), value: 'GEN2' }] : []),
      ...(premiumVoiceEnabled
        ? [{ label: t('PREMIUM'), value: 'PREMIUM' }]
        : []),
    ],
    voiceCloningType: [
      { label: t('proVoiceCloningOption'), value: 'professional' },
      { label: t('instantVoiceCloningOption'), value: 'instant' },
    ],
  };

  const getOptions = () => {
    let omitKeys = [];

    if (isVbee) {
      omitKeys = ['retentionDays', 'ageGroup', 'voiceCloningType'];
    } else if (isMyVoice) {
      omitKeys = ['level', 'retentionDays', 'ageGroup'];
    } else if (isFavourite) {
      omitKeys = [
        'level',
        'voiceCloningType',
        'retentionDays',
        'voiceCloningType',
      ];
    } else {
      omitKeys = ['level', 'voiceCloningType'];
    }

    return omit(advancedFilterOptions, omitKeys);
  };

  const getLabelTag = (key, value) => {
    const option = advancedFilterOptions[key]?.find(
      (item) => item.value === value,
    );
    return option?.label;
  };

  const CategoriesTags = React.memo(({ categories }) => {
    if (!Array.isArray(categories) || categories.length === 0) return null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography variant="body-14-medium" color={COLOR.descriptionGray}>
          {t('categories')}:{' '}
        </Typography>
        {categories.map((item) => (
          <Tag
            key={item}
            label={t(item)}
            onRemove={() => handleRemoveFilter('categories', item)}
          />
        ))}
      </div>
    );
  });

  const LanguageTag = React.memo(({ languageValue }) => {
    if (!languageValue?.primaryLanguageSubTag) return null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography variant="body-14-medium" color={COLOR.descriptionGray}>
          {t('language')}:{' '}
        </Typography>
        <Tag
          key={languageValue}
          label={languageValue?.name?.[language]}
          onRemove={() => {
            handleRemoveFilter('language', '');
            handleRemoveFilter('region', '');
          }}
        />
      </div>
    );
  });

  const RegionTag = React.memo(({ regionValue, languageValue }) => {
    if (!regionValue) return null;
    const region = languageValue?.regions?.find(
      (item) => item.regionSubTag === regionValue,
    );
    if (!region) return null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography variant="body-14-medium" color={COLOR.descriptionGray}>
          {t('region')}:{' '}
        </Typography>
        <Tag
          key={regionValue}
          label={region?.name?.[language]}
          onRemove={() => handleRemoveFilter('region', '')}
        />
      </div>
    );
  });

  const GenderTag = React.memo(({ genderValue }) => {
    if (!genderValue?.length) return null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography variant="body-14-medium" color={COLOR.descriptionGray}>
          {t('gender')}:{' '}
        </Typography>
        {genderValue.map((item) => (
          <Tag
            key={item}
            label={t(item)}
            onRemove={() =>
              handleRemoveFilter('advancedFilter', {
                ...dataFilter.advancedFilter,
                gender: dataFilter.advancedFilter.gender.filter(
                  (gender) => gender !== item,
                ),
              })
            }
          />
        ))}
      </div>
    );
  });

  const AgeGroupTag = React.memo(({ ageGroupValue }) => {
    if (!ageGroupValue?.length) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography variant="body-14-medium" color={COLOR.descriptionGray}>
          {t('ageGroup')}:{' '}
        </Typography>
        {ageGroupValue.map((item) => (
          <Tag
            key={item}
            label={getLabelTag('ageGroup', item)}
            onRemove={() =>
              handleRemoveFilter('advancedFilter', {
                ...dataFilter.advancedFilter,
                ageGroup: dataFilter.advancedFilter.ageGroup.filter(
                  (ageGroup) => ageGroup !== item,
                ),
              })
            }
          />
        ))}
      </div>
    );
  });

  const LevelTag = React.memo(({ levelValue }) => {
    if (!levelValue?.length) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography variant="body-14-medium" color={COLOR.descriptionGray}>
          {t('level')}:{' '}
        </Typography>
        {levelValue.map((item) => (
          <Tag
            key={item}
            label={t(item)}
            onRemove={() =>
              handleRemoveFilter('advancedFilter', {
                ...dataFilter.advancedFilter,
                level: dataFilter.advancedFilter.level.filter(
                  (level) => level !== item,
                ),
              })
            }
          />
        ))}
      </div>
    );
  });

  const RetentionDaysTag = React.memo(({ retentionDaysValue }) => {
    if (
      !retentionDaysValue?.length ||
      retentionDaysValue[0] === DEFAULT_RETENTION_DAYS
    )
      return null;

    const nameTag = (item) => {
      if (item === 365) return t('yearNumber', { value: 1 });
      return t('dayNumber', { days: item });
    };

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography variant="body-14-medium" color={COLOR.descriptionGray}>
          {t('retentionDays')}:{' '}
        </Typography>
        {retentionDaysValue.map((item) => (
          <Tag
            key={item}
            label={nameTag(item)}
            onRemove={() =>
              handleRemoveFilter('advancedFilter', {
                ...dataFilter.advancedFilter,
                retentionDays: [DEFAULT_RETENTION_DAYS],
              })
            }
          />
        ))}
      </div>
    );
  });

  const VoiceCloningTypeTag = React.memo(({ voiceCloningTypeValue }) => {
    if (!voiceCloningTypeValue?.length) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography variant="body-14-medium" color={COLOR.descriptionGray}>
          {t('voiceCloningType')}:{' '}
        </Typography>
        {voiceCloningTypeValue.map((item) => (
          <Tag
            key={item}
            label={getLabelTag('voiceCloningType', item)}
            onRemove={() =>
              handleRemoveFilter('advancedFilter', {
                ...dataFilter.advancedFilter,
                voiceCloningType:
                  dataFilter.advancedFilter.voiceCloningType.filter(
                    (voiceCloningType) => voiceCloningType !== item,
                  ),
              })
            }
          />
        ))}
      </div>
    );
  });

  const hasTags = useMemo(
    () =>
      dataFilter.language?.primaryLanguageSubTag ||
      dataFilter.region ||
      dataFilter.categories?.length > 0 ||
      dataFilter.advancedFilter?.ageGroup?.length > 0 ||
      dataFilter.advancedFilter?.gender?.length > 0 ||
      dataFilter.advancedFilter?.level?.length > 0 ||
      (dataFilter.advancedFilter?.retentionDays?.length > 0 &&
        dataFilter.advancedFilter?.retentionDays[0] !==
          DEFAULT_RETENTION_DAYS) ||
      dataFilter.advancedFilter?.voiceCloningType?.length > 0,
    [dataFilter],
  );

  const sortOptions = useMemo(() => {
    if (isFavourite) return SORT_VOICE_OPTIONS_FAVOURITE;
    if (isVbee) return SORT_VOICE_OPTIONS_VBEE;
    if (isMyVoice) return SORT_VOICE_OPTIONS_MY_VOICE;
    return SORT_VOICE_OPTIONS_COMMUNITY;
  }, [isVbee, isMyVoice, isFavourite]);

  const viewTags = () => (
    <>
      <CategoriesTags categories={dataFilter.categories} />
      <LanguageTag languageValue={dataFilter.language} />
      <RegionTag
        regionValue={dataFilter.region}
        languageValue={dataFilter.language}
      />
      <GenderTag genderValue={dataFilter.advancedFilter?.gender} />
      <AgeGroupTag ageGroupValue={dataFilter.advancedFilter?.ageGroup} />
      <LevelTag levelValue={dataFilter.advancedFilter?.level} />
      <RetentionDaysTag
        retentionDaysValue={dataFilter.advancedFilter?.retentionDays}
      />
      <VoiceCloningTypeTag
        voiceCloningTypeValue={dataFilter.advancedFilter?.voiceCloningType}
      />
      {hasTags && (
        <Typography
          onClick={() => resetDataFilter(activeTab)}
          variant="body-12-medium"
          color={COLOR.textPending}
          style={{ cursor: 'pointer' }}
        >
          {t('deleteAll')}
        </Typography>
      )}
    </>
  );

  return (
    <>
      <StyledFilter hasTags={hasTags}>
        <Search
          handleChangeFilter={handleChangeFilter}
          searchValueView={searchValueView}
          setSearchValueView={setSearchValueView}
        />

        <div className={`filter-category-input ${isMyVoice ? 'my-voice' : ''}`}>
          <Category
            handleChangeFilter={handleChangeFilter}
            dataFilter={dataFilter}
          />
          {!isMyVoice && (
            <>
              <Language
                dataLanguages={dataLanguages}
                dataFilter={dataFilter}
                handleChangeFilter={handleChangeFilter}
              />
              <Accent
                handleChangeFilter={handleChangeFilter}
                dataFilter={dataFilter}
              />
            </>
          )}
          <AdvancedFilterButton
            handleChangeFilter={handleChangeFilter}
            dataFilter={dataFilter}
            options={getOptions()}
            isVbee={isVbee}
            isMyVoice={isMyVoice}
          />
          <SortVoice
            handleChangeFilter={handleChangeFilter}
            dataFilter={dataFilter}
            options={sortOptions}
          />
          {isMyVoice && (
            <Button
              variant="containedSmall"
              startIcon={<AddRounded sx={{ width: '24px', height: '24px' }} />}
              customStyle={{
                padding: '8px 16px',
                height: '40px',
                borderRadius: '8px',
                border: 'none',
              }}
              onClick={handleCreateVoice}
            >
              <Typography
                variant="body-14-semibold"
                style={{ whiteSpace: 'nowrap' }}
              >
                {t('createVoice')}
              </Typography>
            </Button>
          )}
        </div>
      </StyledFilter>
      <StyledListTags>{viewTags()}</StyledListTags>
    </>
  );
};

export default Filter;
